import React, { useState, useEffect, useCallback } from 'react';
import { Button, ProgressBar, Alert, Card, Toast, ToastContainer, Accordion, Badge, ListGroup } from 'react-bootstrap';
import { useGame } from '../contexts/GameContext';
import { useAuth } from '../contexts/AuthContext';
import { Question, LeaderboardEntry, QuestionOption, GameSession } from '../types';
import { gameService } from '../services/gameService';
import { signalRService } from '../services/signalRService';
import './GameFlow.css';

// Interface definitions
interface UserConnection {
  userId: string;
  userName: string;
  connectionId: string;
  isConnected: boolean;
  lastConnected: Date;
  lastDisconnected?: Date;
}

// Define UserConnectionStatus interface
interface UserConnectionStatus {
  userId: string;
  userName?: string;
  isConnected: boolean;
  timestamp: Date;
}

// Interface for answer result
// Signal-R answer result interface (matching what's in signalRService.ts)
interface SignalRAnswerResult {
  isCorrect: boolean;
  pointsEarned: number;
  totalScore: number;
  correctAnswer: string;
  explanation: string;
}

// Local interface for our component's answer result state
interface AnswerResultState {
  isCorrect: boolean;
  points: number;
}

// Interface for player answer summary
interface PlayerAnswer {
  playerId: string;
  playerName: string;
  answerId: number;
  isCorrect: boolean;
}

// Interface for player status
interface PlayerStatus {
  userId: string;
  userName: string;
  isConnected: boolean;
  hasAnswered?: boolean;
  isCorrect?: boolean;
  lastConnected?: Date;
  lastDisconnected?: Date;
}

// Props interface for GameFlow component
interface GameFlowProps {
  gameSession: GameSession; // Properly typed with GameSession interface
}

// Game flow component to handle the active game
const GameFlow: React.FC<GameFlowProps> = ({ gameSession }) => {
  const { submitAnswer, error } = useGame();
  const { user } = useAuth();

  // Game state
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [maxTime, setMaxTime] = useState<number>(30); // Default max time
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [hasAnswered, setHasAnswered] = useState<boolean>(false);
  const [answerResult, setAnswerResult] = useState<AnswerResultState | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [showLeaderboard, setShowLeaderboard] = useState<boolean>(false);
  const [gameEnded, setGameEnded] = useState<boolean>(false);

  // UI state
  const [showGetReady, setShowGetReady] = useState<boolean>(true);
  const [getReadyCountdown, setGetReadyCountdown] = useState<number>(3);
  const [showSkipToast, setShowSkipToast] = useState<boolean>(false);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState<boolean>(false);
  const [showQuestionResults, setShowQuestionResults] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPlayerList, setShowPlayerList] = useState<boolean>(true);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState<boolean>(true);
  const [questionLoadError, setQuestionLoadError] = useState<string | null>(null);
  
  // Player tracking state
  const [playerAnswerSummary, setPlayerAnswerSummary] = useState<PlayerAnswer[]>([]);
  const [playersWhoAnswered, setPlayersWhoAnswered] = useState<Map<string, boolean>>(new Map());
  const [connectedPlayers, setConnectedPlayers] = useState<PlayerStatus[]>([]);
  const [questionResults, setQuestionResults] = useState<PlayerAnswer[]>([]);

  // Derived state
  const isHost = gameSession?.hostUserId === user?.id || false;
  const isInstantMultiplayer = gameSession?.gameType === 'InstantMultiplayer';
  const totalPlayersAnswered = connectedPlayers.filter(p => p.hasAnswered).length;
  const totalPlayers = connectedPlayers.length;
  
  // Host control functions
  
  // Handle skipping the current question (host only)
  const handleSkipQuestion = async () => {
    if (!isHost || !gameSession) return;
    
    try {
      // Call the API to skip the current question
      const hubConnection = (window as any).hubConnection;
      if (hubConnection) {
        await hubConnection.invoke('SkipQuestion', gameSession.id);
        setShowSkipToast(true);
      }
    } catch (error) {
      console.error('Error skipping question:', error);
    }
  };

  // Handle finishing the current question early (host only)
  const handleFinishQuestion = async () => {
    if (!isHost || !gameSession) return;
    
    try {
      // Call the API to finish the current question early
      const hubConnection = (window as any).hubConnection;
      if (hubConnection) {
        await hubConnection.invoke('FinishQuestion', gameSession.id);
      }
    } catch (error) {
      console.error('Error finishing question early:', error);
    }
  };
  
  // Handle starting the game (host only, InstantMultiplayer)
  const handleStartGame = async () => {
    if (!isHost || !gameSession || !isInstantMultiplayer) return;
    
    try {
      console.log('Host starting InstantMultiplayer game:', gameSession.id);
      const hubConnection = (window as any).hubConnection;
      if (hubConnection) {
        await hubConnection.invoke('StartGame', gameSession.id);
      }
    } catch (error) {
      console.error('Error starting game:', error);
    }
  };
  
  // Handle triggering "all players answered" state (host only, InstantMultiplayer)
  const handleAllPlayersAnswered = async () => {
    if (!isHost || !gameSession || !isInstantMultiplayer) return;
    
    try {
      console.log('Host triggering all players answered for session:', gameSession.id);
      const hubConnection = (window as any).hubConnection;
      if (hubConnection) {
        await hubConnection.invoke('ForceAllPlayersAnswered', gameSession.id);
      }
    } catch (error) {
      console.error('Error triggering all players answered:', error);
    }
  };
  
  // Handle moving to next question (host only, InstantMultiplayer)
  const handleMoveToNextQuestion = async () => {
    if (!isHost || !gameSession || !isInstantMultiplayer) return;
    
    try {
      console.log('Host moving to next question for session:', gameSession.id);
      const hubConnection = (window as any).hubConnection;
      if (hubConnection) {
        await hubConnection.invoke('MoveToNextQuestion', gameSession.id);
      }
    } catch (error) {
      console.error('Error moving to next question:', error);
    }
  };

  // Handle starting next question (host only)
  const handleStartNextQuestion = async () => {
    if (!isHost || !gameSession) return;
    
    try {
      console.log('Host starting next question for session:', gameSession.id);
      const hubConnection = (window as any).hubConnection;
      if (hubConnection) {
        await hubConnection.invoke('StartNextQuestion', gameSession.id);
      }
    } catch (error) {
      console.error('Error starting next question:', error);
    }
  };

  // Get ready countdown effect
  useEffect(() => {
    if (showGetReady && getReadyCountdown > 0) {
      const timer = setTimeout(() => {
        setGetReadyCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (showGetReady && getReadyCountdown === 0) {
      setShowGetReady(false);
    }
  }, [showGetReady, getReadyCountdown]);

  // Define fetchInitialQuestion function
  const fetchInitialQuestion = useCallback(async () => {
    console.log('GameFlow: Attempting to fetch current question for game session:', gameSession.id);
    setIsLoadingQuestion(true);
    setQuestionLoadError(null);
    
    try {
      // Try to fetch the current question to get started
      const response = await gameService.getCurrentQuestion(gameSession.id);
      if (response.success && response.data && Object.keys(response.data).length > 0) {
        console.log('GameFlow: Successfully fetched initial question:', response.data);
        // Set up the question UI
        setCurrentQuestion(response.data);
        setMaxTime(response.data.timeLimit);
        setTimeRemaining(response.data.timeLimit);
        setSelectedOption(null);
        setHasAnswered(false);
        setAnswerResult(null);
        setShowLeaderboard(false);
        setIsLoadingQuestion(false);
        return true; // Successfully fetched question
      } else {
        console.log('GameFlow: No active question found, waiting for next question event');
        setQuestionLoadError('No active question found. Waiting for the host to start the next question.');
        setIsLoadingQuestion(false);
        return false; // No question available yet
      }
    } catch (error: any) {
      console.error('GameFlow: Error fetching initial question:', error);
      setQuestionLoadError(`Error loading question: ${error.message || 'Unknown error'}`);
      setIsLoadingQuestion(false);
      return false; // Error occurred
    }
  }, [gameSession?.id]);
  
  // Define retry function
  const retryFetchInitialQuestion = useCallback(async () => {
    const maxRetries = 3;
    let retries = 0;
    while (retries < maxRetries) {
      const success = await fetchInitialQuestion();
      if (success) {
        break;
      }
      retries++;
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retrying
    }
  }, [fetchInitialQuestion]);
  
  // Define fetchQuestion function
  const fetchQuestion = useCallback(async (questionId: number) => {
    setIsLoadingQuestion(true);
    setQuestionLoadError(null);
    
    try {
      const response = await gameService.getQuestion(questionId);
      if (response.success && response.data) {
        const question = response.data;
        setCurrentQuestion(question);
        setMaxTime(question.timeLimit);
        setTimeRemaining(question.timeLimit);
        setSelectedOption(null);
        setHasAnswered(false);
        setAnswerResult(null);
        setShowCorrectAnswer(false);
        setShowQuestionResults(false);
        setIsLoadingQuestion(false);
        
        // Reset player answer tracking for the new question
        setPlayersWhoAnswered(new Map());
        setConnectedPlayers(prev => 
          prev.map(player => ({...player, hasAnswered: false, isCorrect: undefined}))
        );
        
        return true;
      } else {
        setQuestionLoadError('Failed to load question. Please try again.');
        setIsLoadingQuestion(false);
        return false;
      }
    } catch (error: any) {
      console.error('Error fetching question:', error);
      setQuestionLoadError(`Error loading question: ${error.message || 'Unknown error'}`);
      setIsLoadingQuestion(false);
      return false;
    }
  }, []);

  // Handle option selection - just marks the option as selected but doesn't submit yet
  const handleOptionSelect = (optionId: string) => {
    if (hasAnswered || timeRemaining <= 0) return;
    setSelectedOption(optionId);
  };
  
  // Handle submitting the answer after selecting an option
  const handleSubmitAnswer = async () => {
    if (!selectedOption || hasAnswered || timeRemaining <= 0 || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Construct Answer object with required properties
      const answer = {
        sessionId: gameSession.id,
        questionId: currentQuestion?.id || 0,
        selectedOptionId: parseInt(selectedOption, 10),
        submittedAt: new Date()
      };
      
      // Submit the answer using the existing submitAnswer function
      await submitAnswer(answer);
      setHasAnswered(true);
      
      // Update local state to reflect that this player has answered
      if (user) {
        setPlayersWhoAnswered(prev => {
          const newMap = new Map(prev);
          newMap.set(user.id, true);
          return newMap;
        });
        
        // Update connected players with answer status for the current user
        setConnectedPlayers(prev => {
          return prev.map(player => {
            if (player.userId === user.id) {
              return { ...player, hasAnswered: true };
            }
            return player;
          });
        });
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Question timer effect
  useEffect(() => {
    // Only run timer if we have a question and time remaining
    if (currentQuestion && timeRemaining > 0 && !showLeaderboard) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, showLeaderboard]);
  
  // Skip toast effect
  useEffect(() => {
    if (showSkipToast) {
      const timer = setTimeout(() => {
        setShowSkipToast(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [showSkipToast]);

  // Initial setup effect - fetch first question and set up SignalR handlers
  useEffect(() => {
    const initGame = async () => {
      // Attempt to get the current question on initial load
      await fetchInitialQuestion();

      // Set up SignalR handlers for game events
      signalRService.onNextQuestion((data) => {
        console.log('Next question event received:', data);
        if (data && data.questionId) {
          fetchQuestion(data.questionId);
        }
      });

      signalRService.onQuestionEnded((data) => {
        console.log('Question ended event received:', data);
        
        // Force end of question timer (handles both all players answered and time expired scenarios)
        setTimeRemaining(0);
        
        // Show correct answer and set answer result
        setShowCorrectAnswer(true);
        setAnswerResult({
          isCorrect: data.isCorrect,
          points: data.pointsEarned
        });
        
        // If we have player answer summary in data, update our state
        if (data.playerAnswerSummary && Array.isArray(data.playerAnswerSummary)) {
          setPlayerAnswerSummary(data.playerAnswerSummary);
          setQuestionResults(data.playerAnswerSummary);
          
          // Update player correctness in our connected players list
          const playerCorrectness = new Map<string, boolean>();
          data.playerAnswerSummary.forEach((answer: PlayerAnswer) => {
            playerCorrectness.set(answer.playerId, answer.isCorrect);
          });
          
          setConnectedPlayers(prev => {
            return prev.map(player => {
              const isCorrect = playerCorrectness.get(player.userId);
              return { 
                ...player, 
                isCorrect: isCorrect !== undefined ? isCorrect : player.isCorrect
              };
            });
          });
        } else {
          // Otherwise build from our local tracking
          const summary: PlayerAnswer[] = connectedPlayers
            .filter(player => player.hasAnswered)
            .map(player => ({
              playerId: player.userId,
              playerName: player.userName,
              answerId: 0, // We don't have this information from local state
              isCorrect: player.isCorrect || false
            }));
          setPlayerAnswerSummary(summary);
          setQuestionResults(summary);
        }
        
        setShowQuestionResults(true);
        
        // Use setTimeout to show leaderboard after showing correct answer and results
        setTimeout(() => {
          setShowLeaderboard(true);
          setShowQuestionResults(false); // Hide question results when showing leaderboard
        }, 7000); // Extended delay to give users time to see results
      });

      signalRService.onGameEnded(() => {
        console.log('Game ended event received');
        setGameEnded(true);
        setShowLeaderboard(true);
      });

      // Get list of connected players
      signalRService.onConnectedUsers((data) => {
        console.log('Connected users event received:', data);
        if (data && Array.isArray(data)) {
          const connectedUserIds = data.map((user: UserConnection) => user.userId);
          
          // Merge connected users with our player list
          setConnectedPlayers(prev => {
            // Keep existing player info but update connection status
            return prev.map(player => ({
              ...player,
              isConnected: connectedUserIds.includes(player.userId)
            }));
          });
        }
      });

      // Update player answer status when a player answers
      signalRService.onPlayerAnswered((data) => {
        console.log('Player answered event received:', data);
        if (data && data.userId) {
          // Update the list of players who have answered
          setPlayersWhoAnswered(prev => {
            const newMap = new Map(prev);
            newMap.set(data.userId, true);
            return newMap;
          });
          
          // Update connected players with answer status
          setConnectedPlayers(prev => {
            return prev.map(player => {
              if (player.userId === data.userId) {
                return { ...player, hasAnswered: true };
              }
              return player;
            });
          });
        }
      });
    };

    initGame();

    // Cleanup function to remove all SignalR handlers
    return () => {
      signalRService.removeAllListeners();
    };
  }, [fetchInitialQuestion, fetchQuestion]);

  // Render player list with answer status
  const renderPlayerList = () => {
    if (!showPlayerList || connectedPlayers.length === 0) {
      return null;
    }

    return (
      <Accordion defaultActiveKey="0" className="mt-3 mb-3">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            Players ({totalPlayersAnswered}/{totalPlayers} answered)
          </Accordion.Header>
          <Accordion.Body className="player-list">
            <ListGroup>
              {connectedPlayers.map(player => (
                <ListGroup.Item 
                  key={player.userId}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    {player.userName}
                    {user && player.userId === user.id && " (You)"}
                  </div>
                  <div>
                    {player.hasAnswered ? (
                      <Badge bg="success">Answered</Badge>
                    ) : (
                      <Badge bg="secondary">Waiting</Badge>
                    )}
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  // Render question results with player correctness
  const renderQuestionResults = () => {
    if (!showQuestionResults || questionResults.length === 0) {
      return null;
    }

    return (
      <Card className="mt-4 question-results">
        <Card.Header>Question Results</Card.Header>
        <Card.Body>
          <ListGroup>
            {questionResults.map(result => (
              <ListGroup.Item 
                key={result.playerId}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  {result.playerName}
                  {user && result.playerId === user.id && " (You)"}
                </div>
                <div>
                  {result.isCorrect ? (
                    <Badge bg="success">Correct</Badge>
                  ) : (
                    <Badge bg="danger">Incorrect</Badge>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    );
  };

  // If game ended, show final leaderboard
  if (gameEnded) {
    return (
      <div className="game-ended">
        <h2>Game Over!</h2>
        <h3>Final Scores:</h3>
        <div className="leaderboard">
          <ListGroup>
            {leaderboard.map((entry, index) => (
              <ListGroup.Item 
                key={entry.userId}
                className="d-flex justify-content-between align-items-center"
              >
                <span>
                  {index + 1}. {entry.userName}
                  {user && entry.userId === user.id && " (You)"}
                </span>
                <Badge bg="primary">{entry.score} points</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      </div>
    );
  }

  // If showing leaderboard
  if (showLeaderboard) {
    return (
      <div className="leaderboard-container">
        <h3>Leaderboard</h3>
        <div className="leaderboard">
          <ListGroup>
            {leaderboard.map((entry, index) => (
              <ListGroup.Item 
                key={entry.userId}
                className="d-flex justify-content-between align-items-center"
              >
                <span>
                  {index + 1}. {entry.userName}
                  {user && entry.userId === user.id && " (You)"}
                </span>
                <Badge bg="primary">{entry.score} points</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
        
        {isHost && (
          <div className="host-controls mt-4">
            <Button 
              variant="primary" 
              onClick={handleMoveToNextQuestion}
              className="mb-2 w-100"
            >
              Next Question
            </Button>
          </div>
        )}
      </div>
    );
  }

  // If showing correct answer and results
  if (showCorrectAnswer && currentQuestion) {
    const correctOption = currentQuestion.options.find(o => o.isCorrect);
    
    return (
      <div className="correct-answer-overlay">
        <div className="correct-answer-content">
          <h3>The correct answer was:</h3>
          <p className="correct-answer-text">{correctOption?.text}</p>
          
          {answerResult && (
            <div className="answer-result mt-3">
              <h4>Your Result:</h4>
              {answerResult.isCorrect ? (
                <Alert variant="success">
                  <p>Correct! You earned {answerResult.points} points.</p>
                </Alert>
              ) : (
                <Alert variant="danger">
                  <p>Incorrect. Better luck next time!</p>
                </Alert>
              )}
            </div>
          )}
          
          {renderQuestionResults()}
          
          {isHost && (
            <div className="host-controls mt-4">
              <Button 
                variant="primary" 
                onClick={() => setShowLeaderboard(true)}
                className="mb-2 w-100"
              >
                Show Leaderboard
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If we're in "Get Ready" countdown mode
  if (showGetReady) {
    return (
      <div className="get-ready-container">
        <h2>Get Ready!</h2>
        <div className="get-ready-countdown">{getReadyCountdown}</div>
      </div>
    );
  }

  // Show question with option to select and submit answer
  if (currentQuestion) {
    return (
      <div className="question-container">
        {/* Toast for host skipping question */}
        <ToastContainer position="top-end" className="p-3">
          <Toast show={showSkipToast} onClose={() => setShowSkipToast(false)} delay={3000} autohide>
            <Toast.Header>
              <strong className="me-auto">Question Skipped</strong>
            </Toast.Header>
            <Toast.Body>You have skipped this question.</Toast.Body>
          </Toast>
        </ToastContainer>
        
        {/* Timer and question display */}
        <div className="question-header">
          <div className="question-timer">
            <div className="time-remaining">{timeRemaining}s</div>
            <ProgressBar 
              now={(timeRemaining / maxTime) * 100} 
              variant={timeRemaining < 5 ? "danger" : timeRemaining < 10 ? "warning" : "primary"} 
            />
          </div>
        </div>
        
        <div className="question-content">
          <h3 className="question-text">{currentQuestion.text}</h3>
          
          {/* Player list with answer status */}
          {renderPlayerList()}
          
          {/* Options */}
          <div className="options-container">
            {currentQuestion.options.map((option) => (
              <Button
                key={option.id}
                variant={selectedOption === option.id.toString() ? "primary" : "outline-primary"}
                className={`option-button ${selectedOption === option.id.toString() ? "selected" : ""}`}
                onClick={() => handleOptionSelect(option.id.toString())}
                disabled={hasAnswered || timeRemaining <= 0}
              >
                {option.text}
              </Button>
            ))}
          </div>
          
          {/* Submit button */}
          <div className="submit-container">
            <Button
              variant="success"
              size="lg"
              className="submit-button mt-3"
              onClick={handleSubmitAnswer}
              disabled={!selectedOption || hasAnswered || timeRemaining <= 0 || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : hasAnswered ? 'Submitted' : 'Submit Answer'}
            </Button>
          </div>
          
          {/* Host controls */}
          {isHost && (
            <div className="host-controls mt-4">
              <Button 
                variant="warning" 
                onClick={handleFinishQuestion}
                className="mb-2 w-100"
              >
                Finish Question Early
              </Button>
              
              <Button 
                variant="danger" 
                onClick={handleSkipQuestion}
                className="mb-2 w-100"
              >
                Skip Question
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Loading or error state
  return (
    <div className="loading-question">
      {isLoadingQuestion ? (
        <>
          <p>Loading question...</p>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </>
      ) : questionLoadError ? (
        <>
          <p>{questionLoadError}</p>
          {isHost && (
            <div className="d-flex flex-column gap-2 mt-3">
              <Button 
                variant="primary" 
                onClick={() => retryFetchInitialQuestion()}
              >
                Retry Loading Question
              </Button>
              <Button 
                variant="success" 
                onClick={handleStartNextQuestion}
              >
                Start Next Question
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p>Waiting for the next question...</p>
          {isHost && (
            <Button 
              variant="success" 
              onClick={handleStartNextQuestion}
              className="mt-3"
            >
              Start Next Question
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default GameFlow;
